<template>
  <div>
    <!-- Why nothing is happening. The feed shows what DID happen; this says
         what the deliverable is waiting on and why nothing will move it. -->
    <div v-if="blocker" class="blocker">
      <div class="blocker-headline">{{ blocker.headline }}</div>
      <div class="blocker-meta">
        <span v-if="daysInStage !== null">
          {{ daysInStage }} day{{ daysInStage === 1 ? '' : 's' }} in {{ formatStage(blocker.stage) }} ·
        </span>
        {{ blocker.summary }}
      </div>
      <ul class="blocker-items">
        <li v-for="name in blocker.items" :key="name">{{ name }}</li>
      </ul>
      <div class="blocker-note">{{ blocker.note }}</div>
    </div>

    <ActivityTimeline
      :activities="activityLogs"
      :is-loading="loading"
      :compact="compact"
      :limit="limit"
      :user-cache="userCache"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { deliverableService } from '@/services/deliverableService';
import { reviewService } from '@/services/reviewService';
import { userService } from '@/services/userService';
import { logger } from '@/services/loggerService';
import { REVIEW_ITEM_STATUS } from '@/utils/reviewUtils';
import ActivityTimeline from '@/components/common/ActivityTimeline.vue';

// Props
const props = defineProps({
  deliverableId: {
    type: String,
    required: true
  },
  // Needed for the blocker line's stage context. Optional so the other callers
  // (which only ever pass an id) keep working and simply skip the line.
  deliverable: {
    type: Object,
    default: null
  },
  compact: {
    type: Boolean,
    default: false
  },
  limit: {
    type: Number,
    default: 10
  }
});

// State
const activityLogs = ref([]);
const reviewItems = ref([]);
const loading = ref(false);
const userCache = ref(new Map());
let unsubscribe = null;
let unsubscribeItems = null;

// A client/internal decision is only terminal for these. Anything else — including
// a null status — leaves the item pending, which is exactly what stops
// syncReviewStateToDeliverable() from writing the aggregate the rules trigger on.
const PENDING_STATUSES = [
  REVIEW_ITEM_STATUS.PENDING_REVIEW,
  REVIEW_ITEM_STATUS.IN_REVISION,
  REVIEW_ITEM_STATUS.PENDING_FINALIZATION
];

function isPending(status) {
  return !status || PENDING_STATUSES.includes(status);
}

// Capped so a 40-item review doesn't push the activity feed off screen.
const MAX_LISTED_ITEMS = 8;

function nameList(items) {
  const names = items.map((i) => i.name || i.title || 'Untitled');
  if (names.length <= MAX_LISTED_ITEMS) return names;
  return [
    ...names.slice(0, MAX_LISTED_ITEMS),
    `+${names.length - MAX_LISTED_ITEMS} more`
  ];
}

// The deliverable's aggregate review status only syncs once EVERY item has a
// terminal decision (reviewService.syncReviewStateToDeliverable). Until then no
// field changes, so no automation rule can fire and the deliverable sits still.
// That silent gate is the single most common reason a review looks "stuck".
const blocker = computed(() => {
  const stage = props.deliverable?.stage;
  if (!stage || !reviewItems.value.length) return null;

  const total = reviewItems.value.length;

  if (stage === 'client_review') {
    const waiting = reviewItems.value.filter((i) => isPending(i.clientStatus));
    if (!waiting.length) return null;
    return {
      stage,
      headline: 'Waiting on the client',
      summary: `${waiting.length} of ${total} items still need a client decision`,
      items: nameList(waiting),
      note: 'The deliverable cannot advance until every item is approved or has changes requested.'
    };
  }

  if (stage === 'internal_review') {
    const waiting = reviewItems.value.filter((i) => isPending(i.internalStatus));
    if (!waiting.length) return null;
    return {
      stage,
      headline: 'Waiting on internal review',
      summary: `${waiting.length} of ${total} items still need an internal decision`,
      items: nameList(waiting),
      note: 'Nothing is sent to the client until every item is reviewed.'
    };
  }

  return null;
});

// Time in stage comes from the newest stage_change entry rather than a stored
// field — the activity log is already loaded and is the authoritative record.
const daysInStage = computed(() => {
  const entry = activityLogs.value.find((log) => log.action === 'stage_change');
  if (!entry?.timestamp) return null;
  const date = entry.timestamp.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp);
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  return Number.isNaN(days) ? null : days;
});

function formatStage(stage) {
  return String(stage).replace(/_/g, ' ');
}

// Watch for deliverable changes
watch(() => props.deliverableId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    loadActivity();
  }
});

// Lifecycle
onMounted(() => {
  loadActivity();
});

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
  }
  if (unsubscribeItems) {
    unsubscribeItems();
  }
});

// Methods
async function loadActivity() {
  if (!props.deliverableId) return;

  try {
    loading.value = true;

    // Load admin users for the cache
    try {
      const adminUsers = await userService.getAdminUsers();
      adminUsers.forEach(user => {
        userCache.value.set(user.id, user);
      });
      logger.debug('[DeliverableActivityTimeline] Loaded admin users for cache', {
        count: adminUsers.length
      });
    } catch (error) {
      logger.warn('[DeliverableActivityTimeline] Failed to load admin users', {
        error: error.message
      });
    }

    // Unsubscribe from previous listeners
    if (unsubscribe) {
      unsubscribe();
    }
    if (unsubscribeItems) {
      unsubscribeItems();
    }

    // reviewId === deliverableId (1:1). Only needed for the blocker line, so a
    // missing review just leaves reviewItems empty and the line hidden.
    // Own key — the review tab subscribes to the same subcollection, and a
    // shared key would tear its listener down (review tab renders empty).
    unsubscribeItems = reviewService.subscribeToItems(props.deliverableId, (itemsMap) => {
      reviewItems.value = Object.values(itemsMap || {});
    }, { key: `activityItems_${props.deliverableId}` });

    // Subscribe to real-time updates
    unsubscribe = deliverableService.subscribeToDeliverableActivity(
      props.deliverableId,
      (logs) => {
        activityLogs.value = logs;
        loading.value = false;
      }
    );

    // Also fetch initially
    const logs = await deliverableService.getDeliverableActivity(props.deliverableId);
    activityLogs.value = logs;
  } catch (error) {
    logger.error('[DeliverableActivityTimeline] Failed to load activity', {
      deliverableId: props.deliverableId,
      error: error.message
    });
    activityLogs.value = [];
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped lang="scss">
.blocker {
  padding: 0.75rem 0.875rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  border-left: 3px solid hsl(24, 100%, 50%);
  background-color: hsla(24, 100%, 50%, 0.07);
  line-height: 1.5;
}

// Three tiers: what's wrong, the numbers, then the specific items.
.blocker-headline {
  font-weight: 700;
  font-size: 0.9375rem;
}

.blocker-meta {
  font-size: 0.8125rem;
  color: var(--bulma-text-weak);
  margin-top: 0.1rem;
}

.blocker-items {
  margin: 0.5rem 0 0 0;
  padding: 0;
  list-style: none;
  font-size: 0.8125rem;

  li {
    position: relative;
    padding-left: 1rem;
    padding-top: 0.1rem;

    &::before {
      content: '';
      position: absolute;
      left: 0.25rem;
      top: 0.65rem;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background-color: hsl(24, 100%, 50%);
    }
  }
}

.blocker-note {
  font-size: 0.75rem;
  color: var(--bulma-text-weak);
  margin-top: 0.6rem;
}
</style>
